import React, { useMemo } from "react";
import { Select, Dropdown, SelectProps, MenuProps } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { Check } from "lucide-react";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import TruncatedTooltipText from "src/components/reusable/StyledTooltip/TruncatedTooltipText";

interface StyledDropdownProps extends SelectProps {
  dropdownType?: "select" | "menu";
  items?: MenuProps["items"];
  onSelectMenu?: (_key: string) => void;
  children?: React.ReactNode;
}

const getOptionLabelText = (label: React.ReactNode): string => {
  if (typeof label === "string" || typeof label === "number") {
    return String(label);
  }
  return "";
};

const StyledDropdown: React.FC<StyledDropdownProps> = ({
  dropdownType = "select",
  items,
  onSelectMenu,
  children,
  ...props
}) => {
  const options = useMemo(
    () =>
      props.options?.map(option => ({
        ...option,
        // Disable the browser native title tooltip; Datatram StyledTooltip is used instead.
        title: "",
      })),
    [props.options]
  );

  const labelByValue = useMemo(
    () =>
      new Map(
        (props.options ?? []).map(option => [String(option.value), getOptionLabelText(option.label)])
      ),
    [props.options]
  );

  if (dropdownType === "menu" && items && onSelectMenu) {
    const menu = {
      items,
      onClick: ({ key }: { key: string }) => onSelectMenu(key),
    };

    return (
      <Dropdown
        menu={menu}
        trigger={["click"]}
        overlayClassName="custom-dropdown-menu"
      >
        {children}
      </Dropdown>
    );
  }

  // Default to select dropdown
  const isMulti = props.mode === "multiple" || props.mode === "tags";
  const maxTagCount = props.maxTagCount ?? (isMulti ? 1 : undefined);
  const maxTagPlaceholder =
    props.maxTagPlaceholder ??
    ((omitted: { label?: React.ReactNode }[]) => (
      <StyledTooltip content={omitted.map(item => getOptionLabelText(item.label)).join(", ")}>
        <span>{`+${omitted.length}`}</span>
      </StyledTooltip>
    ));

  const defaultTagRender: SelectProps["tagRender"] = ({ label, value, closable, onClose }) => {
    const fullLabel = labelByValue.get(String(value));
    return (
      <span
        className="ant-select-selection-item"
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <StyledTooltip content={fullLabel && fullLabel !== label ? fullLabel : undefined}>
          <span className="ant-select-selection-item-content">{label}</span>
        </StyledTooltip>
        {closable && (
          <span className="ant-select-selection-item-remove" role="button" onClick={onClose}>
            <CloseOutlined />
          </span>
        )}
      </span>
    );
  };
  const suffixIcon =
    props.suffixIcon ?? <span className="styled-dropdown-caret" aria-hidden="true" />;
  const hasValue =
    props.value !== undefined &&
    props.value !== null &&
    (Array.isArray(props.value) ? props.value.length > 0 : props.value !== "");

  const isOptionSelected = (optionValue: string | number | null | undefined) => {
    if (Array.isArray(props.value)) {
      return props.value.some(value => String(value) === String(optionValue));
    }
    if (props.value === undefined || props.value === null || props.value === "") {
      return false;
    }
    return String(props.value) === String(optionValue);
  };

  const defaultOptionRender: SelectProps["optionRender"] = oriOption => {
    const labelText = getOptionLabelText(oriOption.label);
    const selected = isOptionSelected(oriOption.value);

    return (
      <span className="styled-dropdown-option">
        <span className="styled-dropdown-option__check" aria-hidden="true">
          {selected ? <Check size={16} /> : null}
        </span>
        <TruncatedTooltipText text={labelText} className="styled-dropdown-option__label">
          {oriOption.label}
        </TruncatedTooltipText>
      </span>
    );
  };

  const defaultLabelRender: SelectProps["labelRender"] = labelProps => {
    const labelText = getOptionLabelText(labelProps.label);

    return (
      <TruncatedTooltipText text={labelText} className="styled-dropdown-selection-label">
        {labelProps.label}
      </TruncatedTooltipText>
    );
  };

  return (
    <Select
      {...props}
      options={options ?? props.options}
      popupClassName={props.popupClassName || "custom-dropdown-menu"}
      getPopupContainer={
        props.getPopupContainer ||
        (triggerNode => triggerNode?.ownerDocument?.body || document.body)
      }
      suffixIcon={suffixIcon}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={maxTagPlaceholder}
      tagRender={props.tagRender ?? (isMulti ? defaultTagRender : undefined)}
      className={`custom-dropdown ${hasValue ? "custom-dropdown--has-value" : ""} ${props.className || ""}`}
      optionRender={props.optionRender ?? defaultOptionRender}
      labelRender={props.labelRender ?? (!isMulti ? defaultLabelRender : props.labelRender)}
    />
  );
};

export default StyledDropdown;